import React, { useState, useEffect } from 'react'
import {
  TextField,
  Box,
  IconButton,
} from '@mui/material'
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material'

interface NumberInputProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  precision?: number
  stepStrictly?: boolean
  disabled?: boolean
  label?: string
  helperText?: string
  error?: boolean
  size?: 'small' | 'medium'
  fullWidth?: boolean
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  precision = 0,
  stepStrictly = false,
  disabled = false,
  label,
  helperText,
  error = false,
  size = 'medium',
  fullWidth = false,
}) => {
  const [internalValue, setInternalValue] = useState<string>(
    formatValue(value)
  )

  useEffect(() => {
    setInternalValue(formatValue(value))
  }, [value, precision])

  function formatValue(num: number): string {
    if (precision === 0) {
      return Math.round(num).toString()
    }
    return num.toFixed(precision)
  }

  function parseValue(str: string): number {
    const parsed = parseFloat(str)
    return isNaN(parsed) ? 0 : parsed
  }

  function constrainValue(num: number): number {
    let constrained = num

    // Round to precision
    if (precision >= 0) {
      constrained = parseFloat(constrained.toFixed(precision))
    }

    // Apply step strictly if enabled
    if (stepStrictly && step) {
      const remainder = constrained % step
      if (remainder !== 0) {
        constrained = constrained - remainder
      }
    }

    // Apply min/max constraints
    if (min !== undefined && constrained < min) {
      constrained = min
    }
    if (max !== undefined && constrained > max) {
      constrained = max
    }

    return constrained
  }

  const handleIncrement = () => {
    if (disabled) return
    const newValue = constrainValue(value + step)
    onChange(newValue)
  }

  const handleDecrement = () => {
    if (disabled) return
    const newValue = constrainValue(value - step)
    onChange(newValue)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value

    // Allow empty input, minus sign, and decimal point during editing
    if (inputValue === '' || inputValue === '-' || inputValue.endsWith('.')) {
      setInternalValue(inputValue)
      return
    }

    setInternalValue(inputValue)
  }

  const handleInputBlur = () => {
    const parsed = parseValue(internalValue)
    const constrained = constrainValue(parsed)
    setInternalValue(formatValue(constrained))

    if (constrained !== value) {
      onChange(constrained)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      handleIncrement()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      handleDecrement()
    }
  }

  const isDecrementDisabled = disabled || (min !== undefined && value <= min)
  const isIncrementDisabled = disabled || (max !== undefined && value >= max)

  const inputHeight = size === 'small' ? 36 : 40

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <Box
          component="label"
          sx={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: error ? 'error.main' : 'text.primary',
            mb: 0.5,
          }}
        >
          {label}
        </Box>
      )}

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 0.5,
          backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
          '&:hover': {
            borderColor: disabled ? 'divider' : error ? 'error.main' : 'primary.main',
          },
          '&:focus-within': {
            borderColor: error ? 'error.main' : 'primary.main',
            borderWidth: 2,
          },
          overflow: 'hidden',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        {/* Decrement Button */}
        <IconButton
          size="small"
          onClick={handleDecrement}
          disabled={isDecrementDisabled}
          sx={{
            borderRadius: 0,
            height: inputHeight,
            width: inputHeight,
            minWidth: inputHeight,
            padding: 0,
            borderRight: 1,
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <RemoveIcon sx={{ fontSize: size === 'small' ? '1rem' : '1.25rem' }} />
        </IconButton>

        {/* Input Field */}
        <TextField
          value={internalValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          size={size}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{
            flex: 1,
            minWidth: 60,
            '& .MuiInputBase-root': {
              height: inputHeight,
            },
            '& .MuiInputBase-input': {
              textAlign: 'center',
              padding: size === 'small' ? '4px 8px' : '8px 12px',
              fontSize: size === 'small' ? '0.875rem' : '1rem',
            },
          }}
        />

        {/* Increment Button */}
        <IconButton
          size="small"
          onClick={handleIncrement}
          disabled={isIncrementDisabled}
          sx={{
            borderRadius: 0,
            height: inputHeight,
            width: inputHeight,
            minWidth: inputHeight,
            padding: 0,
            borderLeft: 1,
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <AddIcon sx={{ fontSize: size === 'small' ? '1rem' : '1.25rem' }} />
        </IconButton>
      </Box>

      {helperText && (
        <Box
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.75rem',
            color: error ? 'error.main' : 'text.secondary',
            mt: 0.5,
            ml: 1.75,
          }}
        >
          {helperText}
        </Box>
      )}
    </Box>
  )
}

export default NumberInput
